import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Image, MessageSquare, Home, Plus, Trash2, Edit, LogOut } from 'lucide-react';
import { pictureApi, testimonialApi } from '../api';
import Logo from '../components/Logo';

const AdminDashboard = ({ onLogout }) => {
  const [activeTab, setActiveTab] = useState('pictures');
  const [pictures, setPictures] = useState([]);
  const [testimonials, setTestimonials] = useState([]);
  const [showModal, setShowModal] = useState(false);
  const [editingItem, setEditingItem] = useState(null);
  const [formData, setFormData] = useState({});

  useEffect(() => {
    loadData();
  }, []);

  const loadData = async () => {
    try {
      const [picturesRes, testimonialsRes] = await Promise.all([
        pictureApi.getAll(),
        testimonialApi.getAll()
      ]);
      setPictures(picturesRes.data);
      setTestimonials(testimonialsRes.data);
    } catch (error) {
      console.log('Backend not available');
    }
  };

  const handleAdd = () => {
    setEditingItem(null);
    setFormData(activeTab === 'pictures' 
      ? { title: '', description: '', imageUrl: '', category: '' }
      : { clientName: '', clientCountry: '', clientPhoto: '', message: '', rating: 5, tourType: '' }
    );
    setShowModal(true);
  };

  const handleEdit = (item) => {
    setEditingItem(item);
    setFormData(item);
    setShowModal(true);
  };

  const handleDelete = async (id) => {
    if (!window.confirm('Are you sure you want to delete this item?')) return;
    
    try {
      if (activeTab === 'pictures') {
        await pictureApi.delete(id);
        setPictures(pictures.filter(p => p.id !== id));
      } else {
        await testimonialApi.delete(id);
        setTestimonials(testimonials.filter(t => t.id !== id));
      }
    } catch (error) {
      alert('Error deleting item');
    }
  };

  const [imageErrors, setImageErrors] = useState({});

  const handleImageError = (pictureId) => {
    setImageErrors(prev => ({ ...prev, [pictureId]: true }));
  };

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData({ ...formData, [name]: name === 'rating' ? parseInt(value) : value });
  };

  const handleFileChange = (e) => {
    const file = e.target.files[0];
    setFormData({ ...formData, image: file });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    try {
      if (activeTab === 'pictures') {
        if (formData.image) {
          // Handle image upload
          const uploadFormData = new FormData();
          uploadFormData.append('title', formData.title);
          uploadFormData.append('description', formData.description || '');
          uploadFormData.append('category', formData.category);
          uploadFormData.append('image', formData.image);
          
          const res = await pictureApi.upload(uploadFormData);
          if (editingItem) {
            setPictures(pictures.map(p => p.id === editingItem.id ? res.data : p));
          } else {
            setPictures([res.data, ...pictures]);
          }
        } else {
          // Handle regular form submission (for editing without changing image)
          if (editingItem) {
            const res = await pictureApi.update(editingItem.id, formData);
            setPictures(pictures.map(p => p.id === editingItem.id ? res.data : p));
          } else {
            const res = await pictureApi.create(formData);
            setPictures([res.data, ...pictures]);
          }
        }
      } else {
        if (editingItem) {
          const res = await testimonialApi.update(editingItem.id, formData);
          setTestimonials(testimonials.map(t => t.id === editingItem.id ? res.data : t));
        } else {
          const res = await testimonialApi.create(formData);
          setTestimonials([res.data, ...testimonials]);
        }
      }
      setShowModal(false);
    } catch (error) {
      alert('Error saving item');
    }
  };

  return (
    <div className="admin-layout">
      {/* Sidebar */}
      <aside className="admin-sidebar">
        <div className="admin-sidebar-logo">
          <Logo height={52} />
        </div>
        <ul className="admin-nav">
          <li>
            <button 
              className={activeTab === 'pictures' ? 'active' : ''} 
              onClick={() => setActiveTab('pictures')}
            >
              <Image size={20} />
              <span>Pictures</span>
            </button>
          </li>
          <li>
            <button 
              className={activeTab === 'testimonials' ? 'active' : ''} 
              onClick={() => setActiveTab('testimonials')}
            >
              <MessageSquare size={20} />
              <span>Testimonials</span>
            </button>
          </li>
          <li>
            <Link to="/">
              <button>
                <Home size={20} />
                <span>View Site</span>
              </button>
            </Link>
          </li>
          <li>
            <Link to="/admin">
              <button>
                <Home size={20} />
                <span>Admin Portal</span>
              </button>
            </Link>
          </li>
          <li>
            <button onClick={onLogout}>
              <LogOut size={20} />
              <span>Sign Out</span>
            </button>
          </li>
        </ul>
      </aside>

      {/* Main Content */}
      <main className="admin-main">
        <div className="admin-header">
          <h1>{activeTab === 'pictures' ? 'Manage Pictures' : 'Manage Testimonials'}</h1>
          <button className="btn btn-primary" onClick={handleAdd}>
            <Plus size={20} style={{marginRight: '8px'}} />
            Add New
          </button>
        </div>

        <div className="admin-card">
          {activeTab === 'pictures' ? (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Image</th>
                  <th>Title</th>
                  <th>Description</th>
                  <th>Category</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {pictures.length === 0 ? (
                  <tr>
                    <td colSpan="5" style={{textAlign: 'center', padding: '40px'}}>
                      No pictures yet. Click "Add New" to add your first picture.
                    </td>
                  </tr>
                ) : (
                  pictures.map((picture) => (
                    <tr key={picture.id}>
                      <td>
                        <img 
                          src={!imageErrors[picture.id] ? pictureApi.getImage(picture.id) : (picture.imageUrl || 'https://via.placeholder.com/80x60')} 
                          alt={picture.title} 
                          style={{width: '80px', height: '60px', objectFit: 'cover'}} 
                          onError={() => handleImageError(picture.id)}
                        />
                      </td>
                      <td>{picture.title}</td>
                      <td>{picture.description}</td>
                      <td>{picture.category}</td>
                      <td>
                        <button className="action-btn edit" onClick={() => handleEdit(picture)}>
                          <Edit size={16} />
                        </button>
                        <button className="action-btn delete" onClick={() => handleDelete(picture.id)}>
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          ) : (
            <table className="admin-table">
              <thead>
                <tr>
                  <th>Photo</th>
                  <th>Client</th>
                  <th>Country</th>
                  <th>Message</th>
                  <th>Rating</th>
                  <th>Actions</th>
                </tr>
              </thead>
              <tbody>
                {testimonials.length === 0 ? (
                  <tr>
                    <td colSpan="6" style={{textAlign: 'center', padding: '40px'}}>
                      No testimonials yet. Click "Add New" to add your first testimonial.
                    </td>
                  </tr>
                ) : (
                  testimonials.map((testimonial) => (
                    <tr key={testimonial.id}>
                      <td>
                        <img 
                          src={testimonial.clientPhoto || 'https://via.placeholder.com/80'} 
                          alt={testimonial.clientName}
                          style={{borderRadius: '50%', width: '50px', height: '50px'}}
                        />
                      </td>
                      <td>{testimonial.clientName}</td>
                      <td>{testimonial.clientCountry}</td>
                      <td style={{maxWidth: '300px'}}>{testimonial.message?.substring(0, 100)}...</td>
                      <td>{'⭐'.repeat(testimonial.rating || 5)}</td>
                      <td>
                        <button className="action-btn edit" onClick={() => handleEdit(testimonial)}>
                          <Edit size={16} />
                        </button>
                        <button className="action-btn delete" onClick={() => handleDelete(testimonial.id)}>
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>
          )}
        </div>
      </main>

      {/* Modal */}
      {showModal && (
        <div className="modal-overlay" onClick={() => setShowModal(false)}>
          <div className="modal" onClick={(e) => e.stopPropagation()}>
            <h2>{editingItem ? 'Edit' : 'Add'} {activeTab === 'pictures' ? 'Picture' : 'Testimonial'}</h2>
            <form onSubmit={handleSubmit}>
              {activeTab === 'pictures' ? (
                <>
                  <div className="form-group">
                    <label>Title</label>
                    <input 
                      type="text" 
                      name="title" 
                      value={formData.title || ''} 
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Upload Image</label>
                    <input 
                      type="file" 
                      name="image" 
                      onChange={handleFileChange}
                      accept="image/*"
                      required={!editingItem}
                    />
                    {editingItem && (
                      <small style={{display: 'block', marginTop: '5px', color: '#666'}}>
                        Leave empty to keep current image
                      </small>
                    )}
                  </div>
                  <div className="form-group">
                    <label>Description</label>
                    <textarea 
                      name="description" 
                      value={formData.description || ''} 
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>Category</label>
                    <select name="category" value={formData.category || ''} onChange={handleInputChange}>
                      <option value="">Select Category</option>
                      <option value="beach">Beach</option>
                      <option value="culture">Culture</option>
                      <option value="adventure">Adventure</option>
                      <option value="wildlife">Wildlife</option>
                      <option value="food">Food</option>
                    </select>
                  </div>
                </>
              ) : (
                <>
                  <div className="form-group">
                    <label>Client Name</label>
                    <input 
                      type="text" 
                      name="clientName" 
                      value={formData.clientName || ''} 
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Country</label>
                    <input 
                      type="text" 
                      name="clientCountry" 
                      value={formData.clientCountry || ''} 
                      onChange={handleInputChange}
                    />
                  </div>
                  <div className="form-group">
                    <label>Photo URL</label>
                    <input 
                      type="url" 
                      name="clientPhoto" 
                      value={formData.clientPhoto || ''} 
                      onChange={handleInputChange}
                      placeholder="https://example.com/photo.jpg"
                    />
                  </div>
                  <div className="form-group">
                    <label>Message</label>
                    <textarea 
                      name="message" 
                      value={formData.message || ''} 
                      onChange={handleInputChange}
                      required
                    />
                  </div>
                  <div className="form-group">
                    <label>Rating</label>
                    <select name="rating" value={formData.rating || 5} onChange={handleInputChange}>
                      <option value={5}>5 Stars</option>
                      <option value={4}>4 Stars</option>
                      <option value={3}>3 Stars</option>
                      <option value={2}>2 Stars</option>
                      <option value={1}>1 Star</option>
                    </select>
                  </div>
                  <div className="form-group">
                    <label>Tour Type</label>
                    <select name="tourType" value={formData.tourType || ''} onChange={handleInputChange}>
                      <option value="">Select Tour</option>
                      <option value="stone-town">Stone Town Tour</option>
                      <option value="spice-tour">Spice Tour</option>
                      <option value="beach">Beach Tour</option>
                      <option value="snorkeling">Snorkeling</option>
                      <option value="safari">Safari Blue</option>
                    </select>
                  </div>
                </>
              )}
              <div className="modal-actions">
                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>
                  Cancel
                </button>
                <button type="submit" className="btn-save">
                  {editingItem ? 'Update' : 'Save'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default AdminDashboard;
